import { useEffect, useState } from "react";
import api from "../../utils/axiosInstance";
import FormSelect from "../Form/FormSelect";
import FormInput from "../Form/FormInput";
import { btn } from "../Button/buttonStyles";

const toChoice = (value) => (value === true ? "yes" : value === false ? "no" : "default");

// `vendorInvoicing` adds the per-vendor overrides of the vendor portal
// defaults. Left blank, a vendor keeps following the default.
const PartySettingsForm = ({ record, onCancel, onSave, vendorInvoicing = false }) => {
  const [form, setForm] = useState({
    default_currency_id: record?.default_currency_id ? String(record.default_currency_id) : "",
    payment_terms_days: String(record?.payment_terms_days ?? ""),
    can_create_invoices: toChoice(record?.can_create_invoices),
  });
  const defaults = record?.portal_defaults;
  const [currencies, setCurrencies] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/account-settings")
      .then((res) => setCurrencies(res.data.data.currency_options || []))
      .catch(() => setCurrencies([]));
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = {};
    if (!form.default_currency_id) found.default_currency_id = "Default currency is required.";
    const days = form.payment_terms_days.trim();
    if (vendorInvoicing && days && (!/^\d+$/.test(days) || Number(days) > 365))
      found.payment_terms_days = "Enter a whole number of days between 0 and 365.";
    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    await onSave({
      default_currency_id: Number(form.default_currency_id),
      ...(vendorInvoicing && {
        payment_terms_days: days ? Number(days) : null,
        can_create_invoices:
          form.can_create_invoices === "default" ? null : form.can_create_invoices === "yes",
      }),
    });
    setSaving(false);
  };

  const currencyOptions = currencies.map((c) => ({
    value: String(c.id),
    label: `${c.code} - ${c.name}`,
  }));

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900">Edit Settings</h2>
      </div>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-4"
      >
        <div className="grid grid-cols-1 items-start gap-x-5 gap-y-4 md:grid-cols-2">
          <FormSelect
            label="Default Currency"
            name="default_currency_id"
            value={form.default_currency_id}
            onChange={handleChange}
            options={currencyOptions}
            error={errors.default_currency_id}
            required
          />
          {vendorInvoicing && (
            <>
              <FormInput
                label="Default Payment Terms"
                name="payment_terms_days"
                type="number"
                value={form.payment_terms_days}
                onChange={handleChange}
                error={errors.payment_terms_days}
                suffix="days"
                placeholder={
                  defaults ? `Default (${defaults.payment_terms_days} days)` : "Default"
                }
              />
              <FormSelect
                label="Can Create Invoices"
                name="can_create_invoices"
                value={form.can_create_invoices}
                onChange={handleChange}
                options={[
                  {
                    value: "default",
                    label: defaults
                      ? `Default (${defaults.can_create_invoices ? "Yes" : "No"})`
                      : "Default",
                  },
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
                required
              />
            </>
          )}
        </div>

        <div className="flex gap-2 text-sm pt-2 border-t border-gray-100">
          <button type="submit" disabled={saving} className={btn("primary", "sm")}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className={btn("secondary", "sm")}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default PartySettingsForm;